import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { renovarDiferidosFallidos } from "../helpers/lazy-con-reintento";

const TUTORIAL_KEY = "smartdxTutorialDone.v1";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, eventId: null, looksLikeTutorial: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  // #1717 R6: el boundary que envuelve TODAS las rutas recibe `resetKey={pathname}`. Sin esto,
  // un chunk que no bajó en una vista dejaba el mensaje de error pegado en cualquier ruta a la
  // que se navegara después. Sin `resetKey` (el uso de siempre) no cambia nada.
  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, eventId: null, looksLikeTutorial: false });
    }
  }

  componentDidCatch(error, info) {
    // #1717: si lo que falló fue una vista diferida que no bajó, que el próximo intento (volver a
    // la ruta o recargar desde acá) la baje de nuevo en vez de repetir el fallo guardado.
    renovarDiferidosFallidos();
    const stack = `${error?.stack || ""} ${info?.componentStack || ""}`.toLowerCase();
    const looksLikeTutorial =
      stack.includes("tutorial") || stack.includes("intro") || stack.includes("onboarding");

    let eventId = null;
    try {
      eventId = Sentry.captureException(error, {
        contexts: { react: { componentStack: info?.componentStack } },
        tags: { boundary: "ErrorBoundary", looks_like_tutorial: looksLikeTutorial },
      });
    } catch {
      // Sentry no inicializado o falló — no romper el render del fallback.
    }

    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error("[ErrorBoundary]", error, info?.componentStack);
    }

    this.setState({ eventId, looksLikeTutorial });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleSkipTutorial = () => {
    try {
      window.localStorage.setItem(TUTORIAL_KEY, "1");
    } catch {
      // ignore
    }
    window.location.reload();
  };

  handleCopyId = () => {
    if (!this.state.eventId) return;
    try {
      navigator.clipboard?.writeText(this.state.eventId);
    } catch {
      // ignore
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const { eventId, looksLikeTutorial } = this.state;
    return (
      <Box sx={{ maxWidth: 520, mx: "auto", mt: 8, p: 4, textAlign: "center" }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Algo falló al cargar la vista.
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Probá recargar. Si el problema persiste, copiá el ID de error y avisanos.
        </Typography>
        {eventId && (
          <Typography
            variant="caption"
            onClick={this.handleCopyId}
            sx={{
              display: "inline-block",
              fontFamily: "monospace",
              bgcolor: "var(--bg-elevated)",
              px: 1,
              py: 0.5,
              borderRadius: 1,
              mb: 3,
              cursor: "pointer",
            }}
            title="Click para copiar"
          >
            ID: {eventId}
          </Typography>
        )}
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button variant="contained" onClick={this.handleReload}>
            Recargar
          </Button>
          {looksLikeTutorial && (
            <Button variant="outlined" onClick={this.handleSkipTutorial}>
              Desactivar tutorial y recargar
            </Button>
          )}
        </Stack>
      </Box>
    );
  }
}

export default ErrorBoundary;
